import React, { useState, useEffect } from 'react';
import StatusCardMiniView, { StatusCardMiniViewProps } from './StatusCardMiniView';
import { useLastHeatBeat } from '../../../../../features/Jouly/hooks';

export interface StatusCardMiniPresenterProps {
  deviceId?: string;
  isOnline?: boolean;
  lastSeen?: string;
  statusText?: string;
  showLastSeen?: boolean;
  onStatusChange?: (isOnline: boolean) => void;
}

export default function StatusCardMiniPresenter({
  deviceId,
  isOnline: initialIsOnline = false,
  lastSeen: initialLastSeen,
  statusText,
  showLastSeen = false,
  onStatusChange
}: StatusCardMiniPresenterProps) {
  const { isOnline: heartbeatIsOnline, timeSinceLastHeartbeat, lastHeartbeatFormated, isLoading } = useLastHeatBeat(deviceId);
  const [isOnline, setIsOnline] = useState(initialIsOnline);
  const [lastSeen, setLastSeen] = useState(initialLastSeen);

  // Utiliser la logique de heartbeat pour déterminer le statut
  useEffect(() => {
    const checkStatus = () => {
      // Utiliser la logique de heartbeat si disponible, sinon utiliser la valeur initiale
      const currentStatus = heartbeatIsOnline !== undefined ? heartbeatIsOnline : initialIsOnline;
      setIsOnline(currentStatus);
      
      // Utiliser le temps depuis le dernier heartbeat si disponible
      if (timeSinceLastHeartbeat) {
        setLastSeen(timeSinceLastHeartbeat);
      } else if (lastHeartbeatFormated) {
        setLastSeen(lastHeartbeatFormated.toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }));
      } else if (initialLastSeen) {
        setLastSeen(initialLastSeen);
      }
      
      if (onStatusChange) {
        onStatusChange(currentStatus);
      }
    };

    // Vérification initiale
    checkStatus();

    // Vérification périodique toutes les 30 secondes
    const interval = setInterval(checkStatus, 30000);

    return () => clearInterval(interval);
  }, [heartbeatIsOnline, timeSinceLastHeartbeat, lastHeartbeatFormated, initialIsOnline, initialLastSeen, onStatusChange]);

  // Fonction pour obtenir le texte de statut personnalisé (utilisé pour le tooltip)
  const getStatusText = () => {
    // Retourner le statusText si fourni, sinon undefined (le label sera toujours "En ligne" ou "Hors ligne")
    return statusText || undefined;
  };

  // Fonction pour obtenir la dernière activité
  const getLastSeenText = () => {
    if (!showLastSeen) {
      return undefined;
    }
    
    if (lastSeen) {
      return lastSeen;
    }
    
    if (isOnline) {
      return 'Maintenant';
    } else {
      return 'Inconnue';
    }
  };
  if (isLoading) {
    return <>...</>
  }

  // Fonction pour obtenir la date et l'heure formatées
  const getFormattedDate = () => {
    if (lastHeartbeatFormated) {
      return lastHeartbeatFormated.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return null;
  };

  const viewProps: StatusCardMiniViewProps = {
    isOnline,
    deviceId,
    lastSeen: getLastSeenText(),
    statusText: getStatusText(),
    showLastSeen,
    formattedDate: getFormattedDate()
  };

  return <StatusCardMiniView {...viewProps} />;
}

